import { Box, Stack, Typography } from '@mui/material';
import Decimal from 'decimal.js';
import { compact } from 'lodash';
import { ReactNode, useMemo, useState } from 'react';
import { makeStyles } from 'tss-react/mui';

import { DiagramIcon } from '@/components/icons/DiagramIcon';
import { ActionCard } from '@/components/layout/ActionCard/ActionCard';
import { HeaderList } from '@/components/lists/HeaderList/HeaderList';
import { ButtonTab, Tabs } from '@/components/navigation/NavigationTabs';
import { useEntityDetailsContext } from '@/modules/entities/contexts/entityDetails/entityDetails.context';
import { useTestamentaryEntityDetailsContext } from '@/modules/entities/contexts/testamentaryEntityDetails/testamentaryEntityDetails.context';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { COLORS } from '@/styles/tokens/colors';
import { AfterDeath } from '@/types/schema';

import {
  useDispositiveProvisionsContext,
  useUnguardedDispositiveProvisionsContext,
} from '../contexts/dispositiveProvisions.context';
import { isBeneficiaryDesignationEntityKind } from '../dispositiveProvisions.label';
import { GetDispositiveProvisions_EstateWaterfallFragment } from '../DispositiveProvisionsForm/graphql/GetDispositiveProvisions.generated';
import { SpecifyDispositiveProvisionsButton } from '../SpecifyDispositiveProvisionsButton';
import {
  DispositiveProvisionDirection,
  DispositiveProvisionWithEdges,
  UponDeath,
} from './DispositiveProvisionsByDeath.types';
import {
  getCaptionText,
  getDispositiveProvisionsWithEdgesByDeath,
  getProvisionSourceFromAssociatedVizNode,
  getSourceNodeTaxDue,
} from './DispositiveProvisionsByDeath.utils';
import { DispositiveProvisionRow } from './DispositiveProvisionsRow';
import { DispositiveProvisionsTaxDue } from './DispositiveProvisionsTaxDue';
import { ActiveDispositionTab } from './hooks/useDispositiveProvisionsListViewTab';

const useHeaderListClasses = makeStyles()(() => ({
  // eslint-disable-next-line tss-unused-classes/unused-classes
  root: {
    gap: 0,
  },
  // eslint-disable-next-line tss-unused-classes/unused-classes
  childContainer: {
    gap: 0,
  },
  // eslint-disable-next-line tss-unused-classes/unused-classes
  childWrapper: {
    padding: '0px 12px 0 0',
  },
}));

interface DispositiveProvisionsByDeathProps extends ActiveDispositionTab {
  currentNodeValue?: Decimal;
  waterfall: GetDispositiveProvisions_EstateWaterfallFragment;
}

export function DispositiveProvisionsByDeath({
  waterfall,
  ...props
}: DispositiveProvisionsByDeathProps) {
  const { classes: headerListClasses } = useHeaderListClasses();
  const { id, isTwoClientHousehold, allClientIds, dispositionScenarios } =
    useDispositiveProvisionsContext();
  const { entityKind } = useEntityDetailsContext();
  const isBenDesignation = isBeneficiaryDesignationEntityKind(entityKind);
  const noItemsLabel = isBenDesignation
    ? 'No beneficiary designations specified'
    : 'No dispositions specified';
  const { primaryClients } = useHouseholdDetailsContext();
  const { entityId: testamentaryEntityId } =
    useTestamentaryEntityDetailsContext();

  const showCalculatedDispositions =
    useUnguardedDispositiveProvisionsContext()?.showCalculatedDispositions;

  const dispositionScenarioForDeathOrder = dispositionScenarios?.find(
    (scenario) =>
      scenario.firstGrantorDeath?.id === waterfall.firstGrantorDeath?.id
  );

  const canEditProvisions = useMemo(() => {
    if (testamentaryEntityId && isTwoClientHousehold) return true;
    if (allClientIds.includes(id)) return true;
    if (entityKind) return true;

    return false;
  }, [
    allClientIds,
    entityKind,
    id,
    isTwoClientHousehold,
    testamentaryEntityId,
  ]);

  const [direction, setDirection] = useState<DispositiveProvisionDirection>(
    canEditProvisions
      ? DispositiveProvisionDirection.Distributing
      : DispositiveProvisionDirection.Receiving
  );

  const waterfallEdges = useMemo(
    () => waterfall.visualizationWithProjections.edges,
    [waterfall]
  );
  const dispositiveProvisionsByDeath = useMemo<
    Record<UponDeath, DispositiveProvisionWithEdges[]>
  >(() => {
    return getDispositiveProvisionsWithEdgesByDeath(
      waterfallEdges,
      id,
      direction,
      dispositionScenarioForDeathOrder,
      showCalculatedDispositions
    );
  }, [
    waterfallEdges,
    id,
    direction,
    dispositionScenarioForDeathOrder,
    showCalculatedDispositions,
  ]);

  const hasAnyProvisionsInView =
    dispositiveProvisionsByDeath.uponFirstDeath.length > 0 ||
    dispositiveProvisionsByDeath.uponSecondDeath.length > 0;
  const caption = getCaptionText(direction, hasAnyProvisionsInView);

  const { firstDeathItems, secondDeathItems } = useMemo(() => {
    const firstDeathRows = dispositiveProvisionsByDeath.uponFirstDeath.map(
      ({
        dispositiveProvision,
        value,
        from,
        transferTaxKinds,
        proRataTemplateName,
      }) => (
        <DispositiveProvisionRow
          key={`upon-first-death-${dispositiveProvision.id}-${from?.id ?? ''}`}
          direction={direction}
          provision={
            direction === DispositiveProvisionDirection.Distributing
              ? dispositiveProvision
              : getProvisionSourceFromAssociatedVizNode(
                  dispositiveProvision,
                  from
                )
          }
          dyingPrimaryClientId={waterfall.firstGrantorDeath.id}
          primaryClients={primaryClients}
          calculatedProvisionAmount={value}
          transferTaxKinds={transferTaxKinds}
          proRataTemplateName={proRataTemplateName}
        />
      )
    );

    const secondDeathRows = dispositiveProvisionsByDeath.uponSecondDeath.map(
      ({
        dispositiveProvision,
        value,
        from,
        transferTaxKinds,
        proRataTemplateName,
      }) => (
        <DispositiveProvisionRow
          key={`upon-second-death-${dispositiveProvision.id}-${from?.id ?? ''}`}
          direction={direction}
          provision={
            direction === DispositiveProvisionDirection.Distributing
              ? dispositiveProvision
              : getProvisionSourceFromAssociatedVizNode(
                  dispositiveProvision,
                  from
                )
          }
          dyingPrimaryClientId={
            primaryClients?.find((c) => c.id !== waterfall.firstGrantorDeath.id)
              ?.id ?? null
          }
          primaryClients={primaryClients}
          calculatedProvisionAmount={value}
          transferTaxKinds={transferTaxKinds}
          proRataTemplateName={proRataTemplateName}
        />
      )
    );

    let firstDeathTax: ReactNode | null = null;
    let secondDeathTax: ReactNode | null = null;

    if (
      direction === DispositiveProvisionDirection.Distributing &&
      showCalculatedDispositions
    ) {
      const firstDeathTaxDue = getSourceNodeTaxDue(
        id,
        AfterDeath.First,
        waterfall
      );
      const secondDeathTaxDue = getSourceNodeTaxDue(
        id,
        AfterDeath.Second,
        waterfall
      );

      firstDeathTax =
        firstDeathTaxDue.estateTax.greaterThan(0) ||
        firstDeathTaxDue.gstTax.greaterThan(0) ? (
          <DispositiveProvisionsTaxDue {...firstDeathTaxDue} />
        ) : null;
      secondDeathTax =
        secondDeathTaxDue.estateTax.greaterThan(0) ||
        secondDeathTaxDue.gstTax.greaterThan(0) ? (
          <DispositiveProvisionsTaxDue {...secondDeathTaxDue} />
        ) : null;
    }

    const firstDeathItems = compact([...firstDeathRows, firstDeathTax]);
    const secondDeathItems = compact([...secondDeathRows, secondDeathTax]);

    return {
      firstDeathItems,
      secondDeathItems,
    };
  }, [
    direction,
    dispositiveProvisionsByDeath.uponFirstDeath,
    dispositiveProvisionsByDeath.uponSecondDeath,
    id,
    primaryClients,
    showCalculatedDispositions,
    waterfall,
  ]);

  const isViewEmpty =
    firstDeathItems.length === 0 &&
    (!isTwoClientHousehold || secondDeathItems.length === 0);
  const isReceiving = direction === DispositiveProvisionDirection.Receiving;

  return (
    <Stack spacing={2}>
      {canEditProvisions && (
        <Tabs fullWidth>
          <ButtonTab
            display="Distributing"
            isActive={direction === DispositiveProvisionDirection.Distributing}
            onClick={() =>
              setDirection(DispositiveProvisionDirection.Distributing)
            }
          />
          <ButtonTab
            display="Receiving"
            isActive={direction === DispositiveProvisionDirection.Receiving}
            onClick={() =>
              setDirection(DispositiveProvisionDirection.Receiving)
            }
          />
        </Tabs>
      )}
      {isViewEmpty ? (
        <ActionCard
          variant="noCard"
          icon={<DiagramIcon size={32} color={COLORS.TEXT.PRIMARY} />}
          heading={isReceiving ? 'No incoming distributions' : noItemsLabel}
          description={caption}
          sx={{ maxWidth: 400, mx: 'auto' }}
          actions={
            canEditProvisions ? (
              <SpecifyDispositiveProvisionsButton behavior="edit" {...props} />
            ) : undefined
          }
        />
      ) : (
        <>
          <Typography variant="body-xs">{caption}</Typography>
          <Box>
            <HeaderList
              heading={
                isTwoClientHousehold
                  ? `After 1st death (${waterfall?.firstGrantorDeathYear})`
                  : `Upon death (${waterfall.firstGrantorDeathYear})`
              }
              noItemsText={noItemsLabel}
              classes={headerListClasses}
            >
              {firstDeathItems}
            </HeaderList>
            {isTwoClientHousehold && (
              <HeaderList
                heading={`After 2nd death (${waterfall?.secondGrantorDeathYear})`}
                noItemsText={noItemsLabel}
                classes={headerListClasses}
              >
                {secondDeathItems}
              </HeaderList>
            )}
          </Box>
          {canEditProvisions && (
            <SpecifyDispositiveProvisionsButton
              buttonProps={{
                fullWidth: true,
                sx: {
                  px: 2,
                },
              }}
              behavior="edit"
              {...props}
            />
          )}
        </>
      )}
    </Stack>
  );
}
